import { FormEvent, useEffect, useId, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ITAAS_INSTITUTION,
  type ContactDeploymentMode,
  type ContactUrgency,
  type ContactUsersRange,
  type LeadRequestType,
  type ProductCard,
} from "@itaas/pep-contracts";
import { SiteLayout } from "../components/layout/SiteLayout";
import { WhyEcosystemDiagram } from "../components/marketing/WhyEcosystemDiagram";
import { SeoHead, hubPageJsonLd } from "../components/seo/SeoHead";
import { PublisherTrustStrip } from "../components/trust/InstitutionalTrust";
import { getProducts, submitContactRequest } from "../api/pep-api";
import { resolveContactBooking } from "../contact/booking";
import {
  CONTACT_DEPLOYMENT_OPTIONS,
  CONTACT_FAQ,
  CONTACT_HERO_BADGES,
  CONTACT_REASONS,
  CONTACT_SECTORS,
  CONTACT_SUPPORT_IMAGE,
  CONTACT_URGENCY_OPTIONS,
  CONTACT_USERS_OPTIONS,
  contactCopy,
} from "../contact/contact-content";
import { usePepI18n } from "../i18n/usePepI18n";
import { productDisplayName } from "../utils/labels";
import { getAffiliateCodeForLead } from "../utils/affiliate-attribution";

const REQUEST_TYPES = [
  { value: "devis", labelFr: "Demande de devis", labelEn: "Quote request" },
  { value: "demo", labelFr: "Démonstration guidée", labelEn: "Guided demonstration" },
  { value: "info", labelFr: "Information licence", labelEn: "Licence information" },
  { value: "partner", labelFr: "Devenir partenaire", labelEn: "Become a partner" },
];

const COUNTRIES = ["RDC", "Congo", "Gabon", "Cameroun", "Côte d'Ivoire", "Sénégal", "Autre"];

const VALID_TYPES = new Set(REQUEST_TYPES.map((t) => t.value));

/**
 * Contact — premium conversion page.
 * Official intake via submitContactRequest + query-param prefill (product / type / message).
 */
export function ContactPage() {
  const { locale } = usePepI18n();
  const en = locale === "en";
  const copy = contactCopy(locale);
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const faqId = useId();
  const [sent, setSent] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState<string>(CONTACT_FAQ[0]?.id ?? "");
  const [products, setProducts] = useState<ProductCard[]>([]);
  const [form, setForm] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    country: "RDC",
    sector: "",
    type: "demo",
    products: [] as string[],
    deploymentMode: "",
    usersRange: "",
    urgency: "",
    message: "",
  });

  useEffect(() => {
    getProducts().then(setProducts).catch(console.error);
  }, []);

  useEffect(() => {
    const product = searchParams.get("product");
    const type = searchParams.get("type");
    const message = searchParams.get("message");
    const industry = searchParams.get("industry");
    setForm((f) => ({
      ...f,
      type: type && VALID_TYPES.has(type) ? type : f.type,
      products: product ? [product] : f.products,
      message: message ?? f.message,
      sector: industry && CONTACT_SECTORS.some((s) => s.value === industry) ? industry : f.sector,
    }));
  }, [searchParams]);

  const configureHref = `/configure?return=contact${form.products[0] ? `&product=${form.products[0]}` : ""}`;
  const isDevis = form.type === "devis";
  const booking = resolveContactBooking();

  function toggleProduct(slug: string) {
    setForm((f) => ({
      ...f,
      products: f.products.includes(slug)
        ? f.products.filter((p) => p !== slug)
        : [...f.products, slug],
    }));
  }

  function scrollToFormAndFocus() {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(() => nameInputRef.current?.focus(), 350);
  }

  function focusAppointment() {
    setForm((f) => ({ ...f, type: "demo" }));
    scrollToFormAndFocus();
  }

  function onAppointmentClick() {
    if (booking.href) {
      window.open(booking.href, "_blank", "noopener,noreferrer");
      return;
    }
    focusAppointment();
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    submitContactRequest({
      fullName: form.name,
      email: form.email,
      phone: form.phone,
      company: form.company,
      country: form.country,
      sector: form.sector || undefined,
      requestType: form.type as LeadRequestType,
      products: form.products,
      product: form.products[0],
      deploymentMode: form.deploymentMode
        ? (form.deploymentMode as ContactDeploymentMode)
        : undefined,
      usersRange: form.usersRange ? (form.usersRange as ContactUsersRange) : undefined,
      urgency: form.urgency ? (form.urgency as ContactUrgency) : undefined,
      message: form.message.trim() || undefined,
      locale,
      source: "pep-contact",
      affiliateCode: getAffiliateCodeForLead(),
    })
      .then((res) => {
        setReference(res.reference);
        setSent(true);
      })
      .catch(() =>
        setError(en ? "Submission failed. Please retry." : "Échec d'envoi. Réessayez."),
      )
      .finally(() => setSubmitting(false));
  }

  const typeLabel =
    REQUEST_TYPES.find((t) => t.value === form.type)?.[en ? "labelEn" : "labelFr"] ?? form.type;

  return (
    <SiteLayout>
      <SeoHead
        title={copy.seoTitle}
        description={copy.seoDescription}
        keywords={[...copy.seoKeywords]}
        canonicalPath="/contact"
        locale={locale}
        jsonLd={hubPageJsonLd({
          canonicalPath: "/contact",
          name: copy.seoTitle,
          description: copy.seoDescription,
          serviceName: en ? "ITaaS Suite commercial contact" : "Contact commercial ITaaS Suite",
          serviceType: "SalesInquiry",
        })}
      />

      {/* C-HERO */}
      <header className="pep-page-hero pep-contact-hero py-5 mb-0">
        <div className="container">
          <div className="row align-items-center g-4 g-lg-5">
            <div className="col-lg-7">
              <p className="pep-contact-eyebrow mb-2">{copy.heroEyebrow}</p>
              <h1 className="display-5 fw-bold mb-3 pep-contact-hero__title">{copy.heroH1}</h1>
              <p className="lead opacity-90 mb-4">{copy.heroLead}</p>
              <ul className="pep-contact-badges">
                {CONTACT_HERO_BADGES.map((b) => (
                  <li key={b.id} className="pep-contact-badge">
                    {en ? b.labelEn : b.labelFr}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-5">
              <figure className="pep-contact-hero__visual mb-0">
                <WhyEcosystemDiagram centerLabel="ITaaS" locale={locale} />
                <figcaption className="visually-hidden">{copy.heroVisualAlt}</figcaption>
              </figure>
            </div>
          </div>
        </div>
      </header>

      {/* C-FORM + SIDE */}
      <section className="pep-contact-section" aria-labelledby="contact-form-title" id="contact-form">
        <div className="container">
          <div className="row g-4 g-lg-4">
            <div className="col-lg-7">
              {sent ? (
                <div className="pep-contact-form-card p-4 p-md-5">
                  <div className="text-success mb-3" style={{ fontSize: "2.5rem" }} aria-hidden>
                    ✓
                  </div>
                  <h2 className="h4 fw-bold mb-3">{copy.successThanks(form.name)}</h2>
                  {reference ? (
                    <p className="pep-contact-reference mb-3">
                      {copy.successReference}{" "}
                      <strong className="pep-contact-reference__code">{reference}</strong>
                    </p>
                  ) : null}
                  <p className="mb-3">
                    {en ? (
                      <>
                        Your request (<strong>{typeLabel}</strong>)
                        {reference ? (
                          <>
                            {" "}
                            — reference <strong>{reference}</strong>
                          </>
                        ) : null}{" "}
                        has been registered. Our sales team will contact you within{" "}
                        <strong>48 business hours</strong> at <strong>{form.email}</strong>
                        {form.phone ? (
                          <>
                            {" "}
                            or <strong>{form.phone}</strong>
                          </>
                        ) : null}
                        .
                      </>
                    ) : (
                      <>
                        Votre demande (<strong>{typeLabel}</strong>)
                        {reference ? (
                          <>
                            {" "}
                            — référence <strong>{reference}</strong>
                          </>
                        ) : null}{" "}
                        est enregistrée. Notre équipe commerciale vous recontacte sous{" "}
                        <strong>48 h ouvrées</strong> à <strong>{form.email}</strong>
                        {form.phone ? (
                          <>
                            {" "}
                            ou au <strong>{form.phone}</strong>
                          </>
                        ) : null}
                        .
                      </>
                    )}
                  </p>
                  <h3 className="h6 fw-bold mb-2">{copy.successNext}</h3>
                  <ol className="text-secondary mb-4 ps-3">
                    <li className="mb-2">{copy.successStep1}</li>
                    <li className="mb-2">{copy.successStep2}</li>
                    <li>{copy.successStep3}</li>
                  </ol>
                  <div className="d-flex flex-wrap gap-2">
                    <Link to="/products" className="btn btn-outline-primary">
                      {copy.backCatalog}
                    </Link>
                    {form.products.length === 1 ? (
                      <Link to={`/products/${form.products[0]}`} className="btn btn-link">
                        {copy.viewProduct}
                      </Link>
                    ) : null}
                  </div>
                </div>
              ) : (
                <form ref={formRef} onSubmit={onSubmit} className="pep-contact-form-card">
                  <h2 id="contact-form-title" className="pep-contact-form-card__title">
                    {copy.formTitle}
                  </h2>
                  <p className="pep-contact-form-card__lead">{copy.formLead}</p>

                  {isDevis ? (
                    <div className="alert alert-warning border-0 mb-4" role="status">
                      <p className="fw-semibold mb-2">{copy.configureWarnTitle}</p>
                      <p className="small mb-3 mb-md-2">{copy.configureWarnBody}</p>
                      <Link className="btn btn-primary btn-sm" to={configureHref}>
                        {copy.configureWarnCta}
                      </Link>
                      <p className="small text-secondary mt-3 mb-0">{copy.configureWarnKeep}</p>
                    </div>
                  ) : (
                    <div className="alert alert-info border-0 small mb-4">
                      {copy.configureHint}{" "}
                      <Link to={configureHref}>{copy.configureHintLink}</Link>
                    </div>
                  )}

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="contact-name">
                        {copy.labelName}
                      </label>
                      <input
                        id="contact-name"
                        ref={nameInputRef}
                        className="form-control"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        autoComplete="name"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="contact-email">
                        {copy.labelEmail}
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        className="form-control"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                        autoComplete="email"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="contact-phone">
                        {copy.labelPhone}
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        className="form-control"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+243 …"
                        required
                        minLength={8}
                        autoComplete="tel"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="contact-company">
                        {copy.labelCompany}
                      </label>
                      <input
                        id="contact-company"
                        className="form-control"
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        required
                        autoComplete="organization"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="contact-country">
                        {copy.labelCountry}
                      </label>
                      <select
                        id="contact-country"
                        className="form-select"
                        value={form.country}
                        onChange={(e) => setForm({ ...form, country: e.target.value })}
                      >
                        {COUNTRIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="contact-sector">
                        {copy.labelSector}
                      </label>
                      <select
                        id="contact-sector"
                        className="form-select"
                        value={form.sector}
                        onChange={(e) => setForm({ ...form, sector: e.target.value })}
                      >
                        {CONTACT_SECTORS.map((s) => (
                          <option key={s.value || "empty"} value={s.value}>
                            {en ? s.labelEn : s.labelFr}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label" htmlFor="contact-type">
                        {copy.labelType}
                      </label>
                      <select
                        id="contact-type"
                        className="form-select"
                        value={form.type}
                        onChange={(e) => setForm({ ...form, type: e.target.value })}
                      >
                        {REQUEST_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {en ? t.labelEn : t.labelFr}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">{copy.labelProducts}</label>
                      <div className="d-flex flex-wrap gap-2">
                        {products
                          .filter((p) => p.status !== "planned")
                          .map((p) => (
                            <button
                              key={p.slug}
                              type="button"
                              className={`btn btn-sm ${
                                form.products.includes(p.slug) ? "btn-primary" : "btn-outline-secondary"
                              }`}
                              onClick={() => toggleProduct(p.slug)}
                            >
                              {productDisplayName(p, locale)}
                            </button>
                          ))}
                      </div>
                    </div>
                    <div className="col-12">
                      <fieldset className="pep-contact-fieldset">
                        <legend className="form-label">{copy.labelDeployment}</legend>
                        <div className="pep-contact-radio-group">
                          {CONTACT_DEPLOYMENT_OPTIONS.map((opt) => {
                            const id = `contact-deploy-${opt.value}`;
                            return (
                              <div className="form-check" key={opt.value}>
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="contact-deployment"
                                  id={id}
                                  value={opt.value}
                                  checked={form.deploymentMode === opt.value}
                                  onChange={() => setForm({ ...form, deploymentMode: opt.value })}
                                />
                                <label className="form-check-label" htmlFor={id}>
                                  {en ? opt.labelEn : opt.labelFr}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </fieldset>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="contact-users">
                        {copy.labelUsers}
                      </label>
                      <select
                        id="contact-users"
                        className="form-select"
                        value={form.usersRange}
                        onChange={(e) => setForm({ ...form, usersRange: e.target.value })}
                      >
                        <option value="">{en ? "Select…" : "Sélectionner…"}</option>
                        {CONTACT_USERS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {en ? o.labelEn : o.labelFr}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="contact-urgency">
                        {copy.labelUrgency}
                      </label>
                      <select
                        id="contact-urgency"
                        className="form-select"
                        value={form.urgency}
                        onChange={(e) => setForm({ ...form, urgency: e.target.value })}
                      >
                        <option value="">{en ? "Select…" : "Sélectionner…"}</option>
                        {CONTACT_URGENCY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {en ? o.labelEn : o.labelFr}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label" htmlFor="contact-message">
                        {copy.labelMessage}
                      </label>
                      <textarea
                        id="contact-message"
                        className="form-control"
                        rows={4}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder={copy.messagePlaceholder}
                      />
                    </div>
                  </div>

                  <p className="pep-contact-confidential">{copy.confidentiality}</p>
                  {error ? <div className="alert alert-danger mt-3 mb-0">{error}</div> : null}

                  <div className="pep-contact-form-actions">
                    <button
                      type="submit"
                      className="btn pep-contact-btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? copy.submitPending : copy.submitCta}
                      <span className="ms-1" aria-hidden="true">
                        →
                      </span>
                    </button>
                    <button
                      type="button"
                      className="btn pep-contact-btn-secondary"
                      onClick={onAppointmentClick}
                    >
                      {copy.appointCta}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="col-lg-5">
              <div className="d-flex flex-column gap-3">
                <aside className="pep-contact-side-card" aria-labelledby="contact-nap-heading">
                  <h2 id="contact-nap-heading" className="pep-contact-side-card__title">
                    {copy.napTitle}
                  </h2>
                  <p className="pep-contact-side-card__role">{copy.napRole}</p>

                  <p className="pep-contact-nap-label">{copy.napAddress}</p>
                  <ul className="list-unstyled pep-contact-nap-value">
                    {ITAAS_INSTITUTION.address.displayLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>

                  <p className="pep-contact-nap-label">{copy.napPhone}</p>
                  <p className="pep-contact-nap-value">
                    <a href={`tel:${ITAAS_INSTITUTION.telephoneTel}`}>{ITAAS_INSTITUTION.telephone}</a>
                  </p>

                  <p className="pep-contact-nap-label">{copy.napEmail}</p>
                  <p className="pep-contact-nap-value">
                    <a href={`mailto:${ITAAS_INSTITUTION.email}`}>{ITAAS_INSTITUTION.email}</a>
                  </p>

                  <p className="pep-contact-nap-label">{copy.napWeb}</p>
                  <p className="pep-contact-nap-value mb-3">
                    <a href={ITAAS_INSTITUTION.institutionalWebsite} rel="noopener noreferrer">
                      {ITAAS_INSTITUTION.institutionalWebsite.replace(/^https:\/\//, "")}
                    </a>
                  </p>

                  <Link className="pep-home-text-link" to="/about">
                    {copy.napAbout}
                  </Link>
                </aside>

                <aside className="pep-contact-side-card" aria-labelledby="contact-support-heading">
                  <h2 id="contact-support-heading" className="pep-contact-support-title">
                    {copy.supportTitle}
                  </h2>
                  <p className="pep-contact-support-subtitle">{copy.supportSubtitle}</p>
                  <figure className="pep-contact-support-media mb-0">
                    <img
                      src={CONTACT_SUPPORT_IMAGE}
                      alt={copy.supportImageAlt}
                      width={640}
                      height={480}
                      loading="lazy"
                      decoding="async"
                    />
                  </figure>
                  <p className="pep-contact-support-body">{copy.supportBody}</p>
                  <button
                    type="button"
                    className="btn pep-contact-btn-primary"
                    onClick={scrollToFormAndFocus}
                  >
                    {copy.supportCta}
                  </button>
                </aside>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* C-REASONS */}
      <section
        className="pep-contact-section pep-contact-section--muted"
        aria-labelledby="contact-reasons-h2"
      >
        <div className="container">
          <header className="pep-contact-section__head">
            <p className="pep-contact-eyebrow pep-contact-eyebrow--ink">{copy.reasonsEyebrow}</p>
            <h2 id="contact-reasons-h2" className="pep-contact-h2">
              {copy.reasonsH2}
            </h2>
            <p className="pep-contact-lead">{copy.reasonsLead}</p>
          </header>
          <ul className="pep-contact-reason-grid">
            {CONTACT_REASONS.map((r) => (
              <li key={r.id} className="pep-contact-reason">
                <h3 className="pep-contact-reason__title">{en ? r.titleEn : r.titleFr}</h3>
                <p className="pep-contact-reason__body">{en ? r.bodyEn : r.bodyFr}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* C-FAQ + DOCS */}
      <section className="pep-contact-section" aria-labelledby="contact-faq-h2">
        <div className="container">
          <header className="pep-contact-section__head">
            <p className="pep-contact-eyebrow pep-contact-eyebrow--ink">{copy.faqEyebrow}</p>
            <h2 id="contact-faq-h2" className="pep-contact-h2">
              {copy.faqH2}
            </h2>
          </header>
          <div className="pep-contact-faq-grid">
            <div className="accordion pep-contact-accordion" id={faqId}>
              {CONTACT_FAQ.map((item) => {
                const headingId = `${faqId}-h-${item.id}`;
                const collapseId = `${faqId}-c-${item.id}`;
                const isOpen = openFaq === item.id;
                return (
                  <div className="accordion-item" key={item.id}>
                    <h3 className="accordion-header" id={headingId}>
                      <button
                        className={`accordion-button${isOpen ? "" : " collapsed"}`}
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={collapseId}
                        onClick={() => setOpenFaq(isOpen ? "" : item.id)}
                      >
                        {en ? item.qEn : item.qFr}
                      </button>
                    </h3>
                    <div
                      id={collapseId}
                      className={`accordion-collapse collapse${isOpen ? " show" : ""}`}
                      aria-labelledby={headingId}
                    >
                      <div className="accordion-body">{en ? item.aEn : item.aFr}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <aside className="pep-contact-docs" aria-labelledby="contact-docs-title">
              <h3 id="contact-docs-title" className="pep-contact-docs__title">
                {copy.docsTitle}
              </h3>
              <p className="pep-contact-docs__body">{copy.docsBody}</p>
              <Link className="btn btn-outline-primary" to="/docs">
                {copy.docsCta} →
              </Link>
            </aside>
          </div>
        </div>
      </section>

      {/* C-FINAL */}
      <section className="pep-contact-final" aria-labelledby="contact-final-h2">
        <div className="container">
          <div className="p-4 p-md-5 rounded-4 pep-cta-band text-white">
            <div className="row align-items-center g-3">
              <div className="col-lg-8">
                <h2 id="contact-final-h2" className="h3 fw-bold mb-2">
                  {copy.finalH2}
                </h2>
                <p className="opacity-90 mb-0">{copy.finalLead}</p>
              </div>
              <div className="col-lg-4 d-flex justify-content-lg-end">
                <Link className="btn btn-light btn-lg" to="/contact?type=demo">
                  {copy.finalCta}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container py-4">
        <PublisherTrustStrip compact />
      </div>
    </SiteLayout>
  );
}
